import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchSmsCampaigns } from "../../store/smsCampaignSlice";
import CustomNotification from "../../Reuseable/CustomNotification/CustomNotification";
import {
  AddSmsCampaign,
  deleteSmsCampaign,
  EditSmsCampaign,
  changeSmsCampaignStatus,
} from "../../assets/api/campaignService";
import { Button, Dropdown, Skeleton, Space, Tooltip } from "antd";
import {
  DeleteOutlined,
  PlusOutlined,
  SettingOutlined,
  MessageOutlined,
} from "@ant-design/icons";
import PanelCard from "../DashboardPage/components/PanelCard";
import { ReactComponent as EditIcon } from "../../assets/Icons/Editicon.svg";
import moment from "moment";
import ReusableTable from "../../Reuseable/ReusableTable/ReusableTable";
import DeleteCampaignModal from "../../components/CampaignComp/DeleteCampaignModal/DeleteCampaignModal";
import SmsCampaignModal from "../../components/CampaignComp/SmsCampaignModal/SmsCampaignModal";
import "./SMSCampaignPage.scss";

const EMPTY = "—";

const StaticSkeleton = () => <Skeleton.Input size="small" active />;

const TextOrDash = ({ text, loading }) => {
  if (loading) return <StaticSkeleton />;
  if (text === null || text === undefined || text === "") {
    return <span className="smsCampaignEmpty">{EMPTY}</span>;
  }
  return <span className="smsCampaignText">{text}</span>;
};

// Memoized cell components to prevent unnecessary re-renders
const MemoizedTextCell = React.memo(({ text, loading }) => (
  <TextOrDash text={text} loading={loading} />
));
MemoizedTextCell.displayName = "MemoizedTextCell";

const NameCell = React.memo(({ text, loading }) => {
  if (loading) return <StaticSkeleton />;
  if (!text) return <span className="smsCampaignEmpty">{EMPTY}</span>;
  return <span className="smsCampaignName">{text}</span>;
});
NameCell.displayName = "NameCell";

/* Figures are set in tabular numerals so a column of them lines up. */
const NumberCell = React.memo(({ text, loading }) => {
  if (loading) return <StaticSkeleton />;
  if (text === null || text === undefined || text === "") {
    return <span className="smsCampaignEmpty">{EMPTY}</span>;
  }
  return <span className="smsCampaignNumber">{text}</span>;
});
NumberCell.displayName = "NumberCell";

/* The template is an identifier the campaign points at, so it is set apart
   from the prose cells rather than reading as another word in the row. */
const TemplateCell = React.memo(({ text, loading }) => {
  if (loading) return <StaticSkeleton />;
  if (!text) return <span className="smsCampaignEmpty">{EMPTY}</span>;
  return <span className="smsCampaignTemplate">{text}</span>;
});
TemplateCell.displayName = "TemplateCell";

const CreatedAtCell = React.memo(({ text, loading }) => {
  if (loading) return <StaticSkeleton />;
  if (!text) return <span className="smsCampaignEmpty">{EMPTY}</span>;
  const when = moment(text);
  if (!when.isValid()) return <span className="smsCampaignEmpty">{EMPTY}</span>;
  return (
    <span className="smsCampaignDate">
      <span className="smsCampaignDate__day">{when.format("DD MMM YYYY")}</span>
      <span className="smsCampaignDate__time">{when.format("hh:mm A")}</span>
    </span>
  );
});
CreatedAtCell.displayName = "CreatedAtCell";

/* A campaign moves through start / pause / stop / resume, so the chip is
   banded by what the status means rather than being one flat colour. */
const statusBand = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "active":
    case "running":
    case "started":
      return "is-active";
    case "paused":
      return "is-paused";
    case "stopped":
    case "inactive":
      return "is-stopped";
    case "completed":
    case "finished":
      return "is-done";
    default:
      return "is-draft";
  }
};

const StatusCell = React.memo(({ text, loading }) => {
  if (loading) return <StaticSkeleton />;
  if (!text) return <span className="smsCampaignEmpty">{EMPTY}</span>;
  return (
    <span className={`smsCampaignStatus ${statusBand(text)}`}>
      <i />
      {text.charAt(0).toUpperCase() + text.slice(1)}
    </span>
  );
});
StatusCell.displayName = "StatusCell";

const SMSCampaignPage = () => {
  const dispatch = useDispatch();
  const { token, user } = useSelector((state) => state.auth);
  const { sms_campaigns_list, sms_campaign_list_loading } = useSelector(
    (state) => state.smsCampaign,
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isEdit, setIsEdit] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCampaignData, setSelectedCampaignData] = useState(null);
  const [deleteCampaignId, setDeleteCampaignId] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [loadSubmit, setLoadSubmit] = useState(false);


  const handleStatusChange = useCallback(async (id, status) => {
    const payload = { action: status };
    try {
      await changeSmsCampaignStatus(id, payload, token);
      dispatch(fetchSmsCampaigns({ page: currentPage, page_size: pageSize }));
      CustomNotification.show(
        "Sms Campaign status changed successfully",
        "success"
      );
    } catch (err) {
      console.error("Error changing campaign status:", err);
      CustomNotification.show("Error changing sms campaign status", "error");
    }
  }, [token, currentPage, pageSize, dispatch]);

  const handleEditClick = useCallback((record) => {
    console.log("Edit clicked for record:", record);
    setIsEdit(true);
    setSelectedCampaignData(record);
    setIsModalOpen(true);
  }, []);

  const handleDeleteClick = useCallback((record) => {
    console.log("Delete clicked for:", record);
    setDeleteCampaignId(record.id);
    setIsDeleteModalOpen(true);
  }, []);

  const getStatusMenuItems = useCallback((recordId) => [
    {
      key: "start",
      label: "Start",
      onClick: () => handleStatusChange(recordId, "start"),
    },
    {
      key: "pause",
      label: "Pause",
      onClick: () => handleStatusChange(recordId, "pause"),
    },
    {
      key: "stop",
      label: "Stop",
      onClick: () => handleStatusChange(recordId, "stop"),
    },
  ], [handleStatusChange]);

  useEffect(() => {
    dispatch(fetchSmsCampaigns({ page: currentPage, page_size: pageSize }));
  }, [dispatch, currentPage, pageSize]);

  const handleSaveCampaign = useCallback(async (payload) => {
    setLoadSubmit(true);
    try {
      if (isEdit && selectedCampaignData) {
        await EditSmsCampaign(payload, selectedCampaignData.id, token);
        CustomNotification.show("SMS Campaign updated successfully", "success");
      } else {
        await AddSmsCampaign(payload, token);
        CustomNotification.show("SMS Campaign added successfully", "success");
      }
      setIsModalOpen(false);
      setSelectedCampaignData(null);
      setIsEdit(false);
      dispatch(fetchSmsCampaigns({ page: currentPage, page_size: pageSize }));
    } catch (err) {
      console.error("Error saving campaign:", err);
      CustomNotification.show(
        err?.message || "Error saving sms campaign",
        "error"
      );
    } finally {
      setLoadSubmit(false);
    }
  }, [isEdit, selectedCampaignData, token, currentPage, pageSize, dispatch]);

  const handleDeleteCampaign = useCallback(async () => {
    setLoadSubmit(true);
    try {
      const res = await deleteSmsCampaign(deleteCampaignId, token);
      console.log(res, "====campaign delete response====");

      if (res?.status === 200 || res?.status === 201) {
        CustomNotification.show("Campaign deleted successfully", "success");
        setLoadSubmit(false);
        setIsDeleteModalOpen(false);
        setDeleteCampaignId(null);
        dispatch(fetchSmsCampaigns({ page: currentPage, page_size: pageSize }));
      }
    } catch (err) {
      CustomNotification.show(
        err?.message || "Error deleting campaign",
        "error"
      );
      setLoadSubmit(false);
      setIsDeleteModalOpen(false);
      setDeleteCampaignId(null);
    }
  }, [deleteCampaignId, token, currentPage, pageSize, dispatch]);

  const loading = sms_campaign_list_loading;
  const listData = sms_campaigns_list?.data;
  const data = useMemo(() => {
    return Array.isArray(listData) ? listData : listData?.results || [];
  }, [listData]);

  const columns = useMemo(() => [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text) => <NameCell text={text} loading={loading} />,
    },
    {
      title: "Portfolio",
      dataIndex: ["portfolio", "name"],
      key: "portfolio",
      render: (text) => <MemoizedTextCell text={text} loading={loading} />,
    },
    {
      title: "Message Template",
      dataIndex: ["message_template", "name"],
      key: "message_template",
      render: (text) => <TemplateCell text={text} loading={loading} />,
    },
    {
      title: "Leads Count",
      dataIndex: "leads_count",
      key: "leads_count",
      render: (text) => <NumberCell text={text} loading={loading} />,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text) => <StatusCell text={text} loading={loading} />,
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => <CreatedAtCell text={text} loading={loading} />,
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: user?.is_tc ? 80 : 140,
      render: (_, record) => {
        if (loading) return <StaticSkeleton />;

        const isDisabled = user?.is_tc && record?.type?.toLowerCase() === "predictive";

        return (
          <Space size={8}>
            <Tooltip title="Campaign Control">
              <Dropdown
                menu={{ items: getStatusMenuItems(record.id) }}
                trigger={["click"]}
                disabled={isDisabled}
              >
                <Button
                  className="smsCampaignIconBtn is-control"
                  icon={<SettingOutlined />}
                  disabled={isDisabled}
                />
              </Dropdown>
            </Tooltip>
            {!user?.is_tc && (
              <>
                <Tooltip title="Edit campaign">
                  <Button
                    className="smsCampaignIconBtn is-edit"
                    icon={<EditIcon />}
                    onClick={() => handleEditClick(record)}
                  />
                </Tooltip>
                <Tooltip title="Delete campaign">
                  <Button
                    className="smsCampaignIconBtn is-delete"
                    icon={<DeleteOutlined />}
                    onClick={() => handleDeleteClick(record)}
                  />
                </Tooltip>
              </>
            )}
          </Space>
        );
      },
    },
  ], [loading, user?.is_tc, getStatusMenuItems, handleEditClick, handleDeleteClick]);

  const skeletonData = useMemo(() => 
    Array(5).fill({}).map((_, i) => ({ id: `skeleton-${i}` })),
    []
  );

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setIsEdit(false);
    setSelectedCampaignData(null);
  }, []);

  const handleDeleteModalClose = useCallback(() => {
    setIsDeleteModalOpen(false);
    setDeleteCampaignId(null);
  }, []);

  const handleAddCampaign = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleTableChange = useCallback((page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  }, []);

  const totalItems = useMemo(() => 
    sms_campaigns_list?.data?.count || data?.length || 0,
    [sms_campaigns_list, data]
  );

  return (
    <div className="smsCampaignPageMainWrapper">
      <SmsCampaignModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSave={handleSaveCampaign}
        load={loadSubmit}
        isEdit={isEdit}
        initialData={selectedCampaignData}
      />

      <DeleteCampaignModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteModalClose}
        onDelete={handleDeleteCampaign}
        /* was `load`, which the dialog does not read - the Delete button
           never showed its pending state */
        loading={loadSubmit}
      />

      {/* Flush body + an inner `*-report-table` wrapper is how the dashboard
          panels carry a table, so the rows sit at the same inset here. */}
      <PanelCard
        title="All SMS Campaigns"
        icon={<MessageOutlined />}
        accent="brand"
        padded={false}
        actions={
          <Button
            className="addSmsCampaignBtn"
            icon={<PlusOutlined />}
            onClick={handleAddCampaign}
          >
            Add Campaign
          </Button>
        }
      >
        <div className="sms-campaign-report-table">
          <ReusableTable
            columns={columns}
            dataSource={loading ? skeletonData : data}
            totalItems={totalItems}
            rowKey="id"
            CurrentPageNo={currentPage}
            pageSize={pageSize}
            triggerChange={handleTableChange}
          />
        </div>
      </PanelCard>
    </div>
  );
};

export default React.memo(SMSCampaignPage);